import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import type { ReactNode } from 'react'

type SkyMode = 'light' | 'night'

interface ThemeContextValue {
  skyMode: SkyMode
  toggleSky: () => void
  showPhoto: boolean
  togglePhoto: () => void
  fadeStart: number
  setFadeStart: (v: number) => void
  fadeEnd: number
  setFadeEnd: (v: number) => void
  overlayStyle: React.CSSProperties | undefined
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [skyMode, setSkyMode] = useState<SkyMode>(() =>
    localStorage.getItem('tschepp-sky') === 'night' ? 'night' : 'light'
  )
  const [showPhoto, setShowPhoto] = useState(() => {
    const v = localStorage.getItem('tschepp-photo')
    return v === null ? true : v === '1'
  })
  const [fadeStart, setFadeStartState] = useState(() =>
    Number(localStorage.getItem('sg-fade-start') ?? 13)
  )
  const [fadeEnd, setFadeEndState] = useState(() =>
    Number(localStorage.getItem('sg-fade-end') ?? 47)
  )

  // Sync overlay tokens to document root so position:fixed elements inherit them
  useEffect(() => {
    const root = document.documentElement
    if (skyMode === 'night') {
      // Match the .fv-page--night CSS value (frog-view.css): at 0.65 the bright
      // blurred map bled through and washed the bottom-sheet card to a muddy
      // grey ("ausgegraut"). This inline set on <html> governs root-level
      // position:fixed elements (mobile bottom sheet + footer), so it must
      // carry the same 0.86 fix — otherwise desktop (inside .fv-page--night)
      // looks fixed while mobile stays washed (Alex 2026-07-09).
      root.style.setProperty('--ar-overlay-bg', 'rgba(30, 36, 26, 0.86)')
      root.style.setProperty('--ar-overlay-border', 'rgba(255, 255, 255, 0.1)')
      root.style.setProperty('--ar-overlay-shadow', '0 2px 10px rgba(0,0,0,0.4)')
      root.style.setProperty('--ar-overlay-icon', '#F7F5F0')
    } else {
      root.style.removeProperty('--ar-overlay-bg')
      root.style.removeProperty('--ar-overlay-border')
      root.style.removeProperty('--ar-overlay-shadow')
      root.style.removeProperty('--ar-overlay-icon')
    }
  }, [skyMode])

  const toggleSky = useCallback(() => {
    setSkyMode(m => {
      const next = m === 'light' ? 'night' : 'light'
      localStorage.setItem('tschepp-sky', next)
      return next
    })
  }, [])

  const togglePhoto = useCallback(() => {
    setShowPhoto(p => {
      const next = !p
      localStorage.setItem('tschepp-photo', next ? '1' : '0')
      return next
    })
  }, [])

  const setFadeStart = useCallback((v: number) => {
    setFadeStartState(v)
    localStorage.setItem('sg-fade-start', String(v))
  }, [])

  const setFadeEnd = useCallback((v: number) => {
    setFadeEndState(v)
    localStorage.setItem('sg-fade-end', String(v))
  }, [])

  const overlayColor = skyMode === 'light' ? 'rgba(247,245,240,' : 'rgba(8,13,26,'
  const overlayStyle = showPhoto ? {
    background: `linear-gradient(180deg, ${overlayColor}0.95) ${fadeStart}%, ${overlayColor}0) ${fadeEnd}%, ${overlayColor}0) 100%)`
  } as React.CSSProperties : undefined

  return (
    <ThemeContext.Provider value={{ skyMode, toggleSky, showPhoto, togglePhoto, fadeStart, setFadeStart, fadeEnd, setFadeEnd, overlayStyle }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
